import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../apis/axiosConfig';

const TeamMembers = () => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [formData, setFormData] = useState({ 
        name: '', 
        designation: '', 
        image: null,
        institution: '',
        email: '',
        phone: '',
        description: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        fetchTeamMembers();
    }, []);

    const fetchTeamMembers = async () => {
        try {
            const response = await axiosInstance.get('/team');
            setTeamMembers(response.data);
        } catch (error) {
            console.error('Error fetching team members:', error);
            toast.error('Failed to fetch team members');
        }
    };

    const handleInputChange = (e) => {
        if (e.target.name === 'image') {
            setFormData({ ...formData, image: e.target.files[0] });
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const data = new FormData();
        data.append('name', formData.name);
        data.append('designation', formData.designation);
        data.append('institution', formData.institution);
        data.append('email', formData.email);
        data.append('phone', formData.phone);
        data.append('description', formData.description);
        if (formData.image) {
            data.append('image', formData.image);
        }

        try {
            const url = isEditing ? `/team/${editId}` : '/team';
            let response;
            
            if (isEditing) {
                response = await axiosInstance.put(url, data);
            } else {
                response = await axiosInstance.post(url, data);
            }

            if (response.status === 200 || response.status === 201) {
                toast.success(`Team member ${isEditing ? 'updated' : 'added'} successfully`);
                setFormData({ 
                    name: '', 
                    designation: '', 
                    image: null,
                    institution: '',
                    email: '',
                    phone: '',
                    description: ''
                });
                setIsEditing(false);
                setEditId(null);
                setShowForm(false);
                if (document.getElementById('imageInput')) {
                    document.getElementById('imageInput').value = '';
                }
                fetchTeamMembers();
            } else {
                toast.error('Operation failed');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Operation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (member) => {
        setFormData({ 
            name: member.name, 
            designation: member.designation, 
            image: null,
            institution: member.institution || '',
            email: member.email || '',
            phone: member.phone || '',
            description: member.description || ''
        });
        setIsEditing(true);
        setEditId(member.id);
        setShowForm(true);
        window.scrollTo(0, 0);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this team member?')) {
            try {
                const response = await axiosInstance.delete(`/team/${id}`);
                if (response.status === 200 || response.status === 204) {
                    toast.success('Team member deleted successfully');
                    fetchTeamMembers();
                } else {
                    toast.error('Failed to delete team member');
                }
            } catch (error) {
                console.error('Error deleting team member:', error);
                toast.error('Failed to delete team member');
            }
        }
    };

    const resetForm = () => {
        setIsEditing(false);
        setFormData({ 
            name: '', 
            designation: '', 
            image: null,
            institution: '',
            email: '',
            phone: '',
            description: ''
        });
        setShowForm(false);
    };

    return (
        <>
            <div className="container-fluid mb-5 mt-4">
                {/* Premium Animated Header Banner */}
                <div 
                    className="dashboard-header-card d-flex align-items-center justify-content-between mb-4 shadow-lg" 
                    style={{ 
                        background: 'linear-gradient(135deg, #0d47a1 0%, #00695c 100%)', 
                        padding: '1.5rem 2rem', 
                        borderRadius: '15px',
                        animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                >
                    <div className="d-flex align-items-center">
                        <div className="icon-circle mr-3" style={{ background: 'rgba(255,255,255,0.2)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fa fa-users text-white" style={{ fontSize: '24px' }}></i>
                        </div>
                        <div>
                            <h3 className="text-white font-weight-bold mb-0" style={{ letterSpacing: '0.5px' }}>Team Members Directory</h3>
                            <p className="text-white-50 mb-0 small">Manage your team members and experts</p>
                        </div>
                    </div>
                    {!showForm && (
                        <button 
                            className="btn btn-light font-weight-bold shadow-sm" 
                            onClick={() => setShowForm(true)}
                            style={{ color: '#0d47a1', borderRadius: '8px', padding: '10px 20px', transition: 'all 0.3s' }}
                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }} 
                            onFocus={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }} 
                            onBlur={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                            <i className="fa fa-plus mr-2"></i>
                            Add Team Member
                        </button>
                    )}
                </div>
                
                {showForm && (
                    <div className="card shadow mb-4">
                        <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                            <h6 className="m-0 font-weight-bold text-primary">{isEditing ? 'Edit Team Member' : 'Add New Team Member'}</h6>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label htmlFor="teamName">Name</label>
                                        <input type="text" className="form-control" name="name" id="teamName" value={formData.name} onChange={handleInputChange} required />
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label htmlFor="teamDesignation">Designation / Field</label>
                                        <input type="text" className="form-control" name="designation" id="teamDesignation" value={formData.designation} onChange={handleInputChange} required />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label htmlFor="teamInstitution">Institution / University</label>
                                        <input type="text" className="form-control" name="institution" id="teamInstitution" value={formData.institution} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label htmlFor="teamEmail">Email Address</label>
                                        <input type="email" className="form-control" name="email" id="teamEmail" value={formData.email} onChange={handleInputChange} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label htmlFor="teamPhone">Phone / Mobile</label>
                                        <input type="text" className="form-control" name="phone" id="teamPhone" value={formData.phone} onChange={handleInputChange} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="teamDescription">Detailed Bio / Description</label>
                                    <textarea className="form-control" name="description" id="teamDescription" rows="4" value={formData.description} onChange={handleInputChange} placeholder="Enter career bio or professional summary..."></textarea>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="imageInput">Image</label>
                                    <input type="file" className="form-control-file" name="image" id="imageInput" onChange={handleInputChange} accept="image/*" />
                                    {isEditing && <small className="text-muted">Leave blank to keep existing image</small>}
                                </div>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? 'Saving...' : (isEditing ? 'Update Member' : 'Add Member')}
                                </button>
                                <button type="button" className="btn btn-secondary ml-2" onClick={resetForm}>
                                    Cancel
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                    <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between" style={{ background: '#f8f9fc', borderBottom: '2px solid #e3e6f0' }}>
                        <h6 className="m-0 font-weight-bold" style={{ color: '#0d47a1' }}>Team Members List</h6>
                        <span className="badge badge-primary px-3 py-2" style={{ borderRadius: '20px', fontSize: '0.85rem' }}>Total: {teamMembers.length}</span>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0" width="100%" cellSpacing="0">
                                <thead style={{ background: '#f1f3f9' }}>
                                    <tr>
                                        <th style={{ width: '80px', textAlign: 'center', borderBottom: 'none' }}>Image</th>
                                        <th style={{ borderBottom: 'none' }}>Name</th>
                                        <th style={{ borderBottom: 'none' }}>Designation</th>
                                        <th style={{ width: '150px', textAlign: 'center', borderBottom: 'none' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {teamMembers.map((member) => (
                                        <tr key={member.id}>
                                            <td className="text-center align-middle">
                                                {member.image ? (
                                                    <img src={`${process.env.REACT_APP_BACKEND || 'http://localhost:8000/'}team/${member.image}`} alt={member.name} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }} />
                                                ) : (
                                                    <div style={{ width: '50px', height: '50px', background: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '10px' }}>No Img</div>
                                                )}
                                            </td>
                                            <td className="align-middle font-weight-bold text-dark">{member.name}</td>
                                            <td className="align-middle">{member.designation}</td>
                                            <td className="align-middle text-center">
                                                <button className="btn btn-sm btn-outline-primary mr-2 shadow-sm" style={{ borderRadius: '6px' }} onClick={() => handleEdit(member)}>
                                                    <i className="fa fa-edit mr-1"></i>Edit
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger shadow-sm" style={{ borderRadius: '6px' }} onClick={() => handleDelete(member.id)}>
                                                    <i className="fa fa-trash mr-1"></i>Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {teamMembers.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-muted">No team members found. Click &quot;Add Team Member&quot; to create one.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TeamMembers;
